import { addDays, eachDayOfInterval, format, isBefore, isSameDay, isWeekend } from "date-fns";
import React, { useCallback, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { DayContainer, DateStripContainer, Navigation, DateStripDaysContainer } from "./styles";
import { useLocaleProvider } from "../../../components/locale-provider";
import { es } from "date-fns/locale";
import { LOCALE } from "../../witadmin/Common/constants";
import { usePropertyConfiguration } from "../../../components/property-configuration-provider";
import { calculateInputWidth } from "../shared/table";

export function DateStrip({
  startDate,
  setStartDate,
  loading,
  numberOfDays,
  lite,
  showAltStyles,
  onClickDay
}: {
  startDate: Date;
  setStartDate: (date: Date) => void;
  loading: boolean;
  numberOfDays: number;
  lite?: boolean;
  showAltStyles?: boolean;
  onClickDay: (date: Date) => void;
}) {
  const locale = useLocaleProvider().currentLocale?.substring(0, 2);
  const endDate = useMemo(() => addDays(startDate, numberOfDays - 1), [startDate, numberOfDays]);
  const startDateIsToday = useMemo(() => isSameDay(startDate, new Date()), [startDate]);
  const dayWidth = useMemo(() => calculateInputWidth(numberOfDays), [numberOfDays]);
  const days = useMemo(
    () =>
      eachDayOfInterval({
        start: new Date(startDate),
        end: new Date(endDate),
      }),
    [startDate, endDate]
  );
  const fillBgColor = useCallback((day: Date) =>
    Boolean(showAltStyles && isWeekend(day)),
    [showAltStyles]
  );
  const daysFormatted = useMemo(() => days.map(day => (
    <DayContainer isWeekend={fillBgColor(day)} lite={lite} onClick={() => onClickDay(day)} width={dayWidth}>
      <div>{format(day, "eee", { locale: locale === LOCALE.es ? es : undefined })}</div>
      <div>
        {format(day, "d", { locale: locale === LOCALE.es ? es : undefined })}
      </div>
      <div>
        {format(day, "LLL", { locale: locale === LOCALE.es ? es : undefined }).toUpperCase()}
      </div>
    </DayContainer>
  )), [fillBgColor, days, locale, onClickDay]);

  const handleNavigate = useCallback(
    (selectedDays: number) => {
      if (loading) {
        return;
      }
      const newStartDate = addDays(startDate, selectedDays);
      const today = new Date();
      if (isBefore(newStartDate, today)) {
        setStartDate(today);
      } else {
        setStartDate(newStartDate);
      }
    },
    [startDate, setStartDate, loading]
  );
  return (
    <DateStripContainer>
      <Navigation>
        {!startDateIsToday && (
          <>
            <button aria-label="previous-day" onClick={() => handleNavigate(-1)}>
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            <button aria-label="previous-weeks" onClick={() => handleNavigate(-numberOfDays)}>
              <FontAwesomeIcon icon={faChevronLeft} />
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
          </>
        )}
      </Navigation>
      <DateStripDaysContainer>
        {daysFormatted.map((day, index) => (
          <div className={"day-strip-day"} key={index}>
            {day}
          </div>
        ))}
      </DateStripDaysContainer>
      <Navigation>
        <button aria-label="next-weeks" onClick={() => handleNavigate(numberOfDays)}>
          <FontAwesomeIcon icon={faChevronRight} />
          <FontAwesomeIcon icon={faChevronRight} />
        </button>
        <button aria-label="next-day" onClick={() => handleNavigate(1)}>
          <FontAwesomeIcon icon={faChevronRight} />
        </button>
      </Navigation>
    </DateStripContainer>
  );
}
